import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n";
import { useProperty } from "@/context/PropertyContext";
import { API } from "@/lib/apiClient";
import api from "@/lib/apiClient";
import { Lu } from "@/components/shared";

const SUGGEST = {
  ar: ["ما الغرف الشاغرة والتكييف يعمل فيها؟", "كم وفّرنا من الطاقة هذا الأسبوع؟", "ما أهم تنبيهات الصيانة الآن؟", "أي طابق الأعلى استهلاكاً؟"],
  en: ["Which vacant rooms have AC on?", "How much energy did we save this week?", "Top maintenance alerts right now?", "Which floor consumes the most?"],
};

function sessionFor(propId) {
  const k = `md_chat_${propId}`;
  let s = localStorage.getItem(k);
  if (!s) { s = `${propId}-${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem(k, s); }
  return s;
}

export function AssistantChat() {
  const { lang } = useLang();
  const { selected } = useProperty();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const L = lang === "ar";
  const sid = selected ? sessionFor(selected.id) : null;

  useEffect(() => {
    if (!selected || !open) return;
    api.get(`/properties/${selected.id}/assistant/history?session_id=${sid}`).then((r) => setMsgs(r.data)).catch(() => {});
  }, [selected?.id, open]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy || !selected) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/properties/${selected.id}/assistant/chat`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("md_token")}` },
        body: JSON.stringify({ session_id: sid, message: q }),
      });
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n"); buf = lines.pop();
        for (const ln of lines) {
          const d = ln.replace(/^data: /, "");
          if (d === "[DONE]") continue;
          try { const { t } = JSON.parse(d); setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + t }; return c; }); } catch {}
        }
      }
    } catch {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: L ? "تعذر الاتصال بالمساعد." : "Could not reach the assistant." }; return c; });
    } finally { setBusy(false); }
  };

  const reset = () => { localStorage.removeItem(`md_chat_${selected.id}`); setMsgs([]); };

  return (
    <>
      <button data-testid="assistant-fab" onClick={() => setOpen((o) => !o)} aria-label="Madarek Assistant"
        className="fixed bottom-6 ltr:right-6 rtl:left-6 z-50 w-14 h-14 rounded-full md-gold-gradient md-gold-glow text-white flex items-center justify-center hover:scale-105 active:scale-95 transition-transform">
        <Lu name={open ? "x" : "message-square-text"} className="w-6 h-6" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }} transition={{ duration: 0.25 }}
            data-testid="assistant-panel"
            className="fixed bottom-24 ltr:right-6 rtl:left-6 z-50 w-[min(420px,calc(100vw-2rem))] h-[min(600px,calc(100vh-8rem))] md-card shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card">
              <span className="w-9 h-9 rounded-lg md-gold-gradient flex items-center justify-center"><Lu name="bot" className="w-5 h-5 text-white" /></span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground leading-tight">{L ? "مساعد مدارك" : "Madarek Assistant"}</p>
                <p className="text-[11px] text-muted-foreground truncate">{selected?.[L ? "name_ar" : "name_en"]} · Claude Sonnet 4.6</p>
              </div>
              <button data-testid="assistant-reset" onClick={reset} title={L ? "محادثة جديدة" : "New chat"} className="p-2 rounded-md hover:bg-foreground/10 text-muted-foreground"><Lu name="rotate-ccw" className="w-4 h-4" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
              {msgs.length === 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{L ? "اسألني عن الغرف، الطاقة، التنبيهات أو التذاكر:" : "Ask me about rooms, energy, alerts or tickets:"}</p>
                  {SUGGEST[lang].map((s) => (
                    <button key={s} data-testid="assistant-suggestion" onClick={() => send(s)}
                      className="block w-full text-start text-sm px-3 py-2 rounded-lg bg-foreground/5 hover:bg-primary/10 border border-border hover:border-primary/40 text-foreground/80">{s}</button>
                  ))}
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`assistant-msg-${m.role}`}
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground rounded-ee-md" : "bg-foreground/5 text-foreground border border-border rounded-es-md"}`}>
                    {m.content ? m.content.replace(/\*\*/g, "").replace(/^#+\s*/gm, "") : <span className="inline-flex gap-1 items-center h-4"><span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:120ms]" /><span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:240ms]" /></span>}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-border bg-card flex gap-2">
              <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} disabled={busy}
                placeholder={L ? "اكتب سؤالك..." : "Type your question..."}
                className="flex-1 h-10 px-3 rounded-lg bg-foreground/5 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
              <button data-testid="assistant-send" type="submit" disabled={busy || !input.trim()}
                className="w-10 h-10 rounded-lg md-gold-gradient text-white flex items-center justify-center disabled:opacity-40">
                <Lu name="send-horizontal" className={`w-4 h-4 ${L ? "rotate-180" : ""}`} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
